import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Zap, 
  Shield, 
  Users, 
  Code, 
  ArrowRight 
} from 'lucide-react';
import ParticleField from '../components/ParticleField';
import SpaceBackground from '../components/SpaceBackground';
import { useIsMobile } from '../hooks/useIsMobile';

const valueProps = [
  {
    icon: Code,
    title: 'Custom Where It Counts',
    description: 'Tailored solutions built specifically for your business needs, not generic templates.',
  },
  {
    icon: Zap,
    title: 'Genuine Support',
    description: 'Real people, real help; when you need it most.',
  },
  {
    icon: Shield,
    title: 'Security',
    description: 'Enterprise-grade security practices built into every project.',
  },
  {
    icon: Users,
    title: 'Partnership',
    description: 'Long-term collaboration and support through retainer agreements.',
  },
];

const processSteps = [
  { name: 'Discover', description: 'Understanding your needs and goals' },
  { name: 'Build', description: 'Crafting your custom solution' },
  { name: 'Launch', description: 'Deploying to the cloud' },
  { name: 'Iterate', description: 'Continuous improvement and support' },
];

export default function Home() {
  const isMobile = useIsMobile();
  
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Mobile: Simple static gradient background */}
        {isMobile ? (
          <div className="absolute inset-0 bg-gradient-to-b from-[#000510] via-[#0a0e27] to-[#1a1f3a]">
            {/* Static stars */}
            <div className="absolute inset-0">
              {[...Array(50)].map((_, i) => (
                <div
                  key={`star-${i}`}
                  className="absolute w-1 h-1 bg-white rounded-full"
                  style={{
                    left: `${Math.random() * 100}%`,
                    top: `${Math.random() * 100}%`,
                    opacity: Math.random() * 0.7 + 0.3,
                  }}
                />
              ))}
            </div>
            {/* Subtle dark overlay for text contrast */}
            <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/30 to-transparent" />
          </div>
        ) : (
          <>
            {/* Desktop: Full animated space background */}
            <SpaceBackground />
            <ParticleField />
          </>
        )}
        
        {/* Spotlight effect */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_800px_600px_at_50%_40%,rgba(59,130,246,0.15),transparent)]" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            {/* Floating badge - hidden on very small screens */}
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
              className="hidden sm:inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-launch-blue/20 via-launch-purple/20 to-launch-blue/20 border border-launch-purple/50 backdrop-blur-md mb-8 shadow-[0_0_20px_rgba(168,85,247,0.2)]"
            >
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-launch-purple opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-gradient-to-r from-launch-blue to-launch-purple"></span>
              </span>
              <span className="text-sm bg-gradient-to-r from-launch-blue via-launch-purple to-launch-blue bg-clip-text text-transparent font-semibold">Custom Solutions • Modern Tech</span>
            </motion.div>
            
            {/* Small screen spacing when badge is hidden */}
            <div className="sm:hidden mb-6" />

            <h1 className="text-4xl sm:text-5xl md:text-7xl font-bold mb-4 sm:mb-6">
              {/* Mobile version - simple bold gradient, big impact */}
              <span className="md:hidden block bg-gradient-to-br from-white via-blue-100 to-purple-200 bg-clip-text text-transparent font-black tracking-tight leading-[1.1]" style={{
                textShadow: '0 10px 40px rgba(0,0,0,0.3)',
              }}>
                Custom Web Design & Tech Support
              </span>
              
              {/* Desktop version - original subtle style */}
              <span className="hidden md:inline bg-gradient-to-r from-launch-blue via-launch-purple to-launch-orange bg-clip-text text-transparent drop-shadow-[0_0_30px_rgba(59,130,246,0.5)]">
                Custom Web Design
                <br />
              </span>
              <span className="hidden md:inline text-offwhite">& Tech Support</span>
            </h1>
            
            <p className="text-base sm:text-lg md:text-2xl text-white/95 mb-6 sm:mb-8 max-w-3xl mx-auto leading-relaxed px-4 sm:px-0 font-medium" style={{
              textShadow: '0 2px 8px rgba(0,0,0,0.9)',
            }}>
              Professional web design and development services, backed by expert 
              technical support retainers for ongoing peace of mind.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Link
                  to="/contact"
                  className="group inline-flex items-center px-8 py-3 rounded-lg bg-gradient-to-r from-launch-blue to-launch-purple text-white font-medium shadow-[0_0_40px_rgba(59,130,246,0.3)] hover:shadow-[0_0_60px_rgba(59,130,246,0.5)] transition-shadow"
                >
                  Get Started
                  <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
                </Link>
              </motion.div>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Link
                  to="/portfolio"
                  className="inline-flex items-center px-8 py-3 rounded-lg border-2 border-launch-purple/70 text-white font-medium bg-gradient-to-r from-launch-purple/20 to-launch-orange/20 hover:from-launch-purple/30 hover:to-launch-orange/30 hover:border-launch-purple backdrop-blur-sm transition-all shadow-[0_0_20px_rgba(168,85,247,0.2)] hover:shadow-[0_0_35px_rgba(168,85,247,0.4)]"
                >
                  View Portfolio
                </Link>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Value Props */}
      <section className="py-24 bg-night-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h2
            className="text-4xl font-bold text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Why Choose <span className="text-launch-blue">Cloud Launch</span>
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {valueProps.map((prop, index) => (
              <motion.div
                key={prop.title}
                className="bg-night-700 rounded-xl p-6 hover:bg-night-600 cursor-pointer"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ delay: index * 0.1, duration: 0.5, ease: "easeOut" }}
                whileHover={{ 
                  scale: 1.05,
                  boxShadow: "0 10px 30px rgba(59, 130, 246, 0.2)",
                  transition: { duration: 0.3 }
                }}
              >
                <motion.div
                  whileHover={{ rotate: [0, -10, 10, -10, 0] }}
                  transition={{ duration: 0.5 }}
                >
                  <prop.icon className="w-12 h-12 text-launch-blue mb-4" />
                </motion.div>
                <h3 className="text-xl font-semibold mb-2">{prop.title}</h3>
                <p className="text-offwhite/70">{prop.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-24 bg-night-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Our Process</h2>
            <p className="text-offwhite/70 text-lg">How we work together</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {processSteps.map((step, index) => (
              <motion.div
                key={step.name}
                className="relative"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-r from-launch-blue to-launch-purple flex items-center justify-center text-white font-bold">
                    {index + 1}
                  </div>
                  {index < processSteps.length - 1 && (
                    <div className="hidden md:block flex-1 h-1 bg-gradient-to-r from-launch-blue to-launch-purple ml-4" />
                  )}
                </div>
                <h3 className="text-xl font-semibold mb-2">{step.name}</h3>
                <p className="text-offwhite/70">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-launch-blue via-launch-purple to-launch-orange">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold text-white mb-6">
              Ready to Launch Your Project?
            </h2>
            <p className="text-white/90 text-xl mb-8">
              Let's discuss how we can help transform your business with modern technology.
            </p>
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Link
                to="/contact"
                className="inline-flex items-center px-8 py-3 rounded-lg bg-white text-launch-blue font-medium hover:bg-offwhite"
              >
                Start Your Project
                <ArrowRight className="ml-2" size={20} />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
